// Anniversary hooks — TanStack Query wrappers around the anniversary API.
//
// Mirrors `lib/hooks/album.ts` shape: query hooks + mutation hooks + invalidation
// via the `queryKeys.anniversary.*` factory (extended in keys.ts).

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryOptions,
  type UseQueryOptions,
} from "@tanstack/react-query";

import {
  classifyCategory,
  createAnniversary,
  getAnniversary,
  getAnniversaryDiaries,
  getNext,
  getTimeline,
  getToday,
  listActivity,
  listAnniversaries,
  listMilestones,
  restoreAnniversary,
  softDeleteAnniversary,
  updateAnniversary,
  type AnniversaryClassifyInput,
  type AnniversaryClassifyResponse,
  type AnniversaryCreateInput,
  type AnniversaryDiaryListResponse,
  type AnniversaryListOpts,
  type AnniversaryListResponse,
  type AnniversaryActivityListResponse,
  type AnniversaryMilestoneListResponse,
  type AnniversaryNextResponse,
  type AnniversaryRead,
  type AnniversaryTimelineResponse,
  type AnniversaryTodayResponse,
  type AnniversaryUpdateInput,
} from "@/lib/api/anniversary";
import { queryKeys } from "@/lib/queries/keys";

// ----- Queries ------------------------------------------------------------

export function useAnniversariesList(
  opts: AnniversaryListOpts = {},
  options?: Partial<UseQueryOptions<AnniversaryListResponse, Error>>,
) {
  return useQuery<AnniversaryListResponse, Error>({
    queryKey: [...queryKeys.anniversary.list(), opts] as const,
    queryFn: () => listAnniversaries(opts),
    staleTime: 30_000,
    ...options,
  });
}

export function useAnniversaryDetail(
  id: string | undefined,
  options?: Partial<UseQueryOptions<AnniversaryRead, Error>>,
) {
  return useQuery<AnniversaryRead, Error>({
    queryKey: queryKeys.anniversary.detail(id ?? ""),
    queryFn: () => getAnniversary(id ?? ""),
    enabled: Boolean(id),
    ...options,
  });
}

export function useAnniversaryTimeline(
  opts: { future_years?: number; past_years?: number; category?: string[] } = {},
  options?: Partial<UseQueryOptions<AnniversaryTimelineResponse, Error>>,
) {
  return useQuery<AnniversaryTimelineResponse, Error>({
    queryKey: [...queryKeys.anniversary.timeline(), opts] as const,
    queryFn: () => getTimeline(opts),
    staleTime: 60_000,
    ...options,
  });
}

export function useAnniversaryToday(
  options?: Partial<UseQueryOptions<AnniversaryTodayResponse, Error>>,
) {
  return useQuery<AnniversaryTodayResponse, Error>({
    queryKey: queryKeys.anniversary.today(),
    queryFn: getToday,
    staleTime: 60_000,
    ...options,
  });
}

export function useAnniversaryNext(
  options?: Partial<UseQueryOptions<AnniversaryNextResponse, Error>>,
) {
  return useQuery<AnniversaryNextResponse, Error>({
    queryKey: queryKeys.anniversary.next(),
    queryFn: getNext,
    staleTime: 60_000,
    ...options,
  });
}

/**
 * Milestones, paged.
 *
 * W46.0: this was a plain `useQuery` with no cursor, so it read page 1 and
 * dropped the rest — and the backend it called returned a hard-coded
 * `next_cursor: null` anyway, so there was nothing to follow. Both halves are
 * fixed; this half is "actually follow it". A milestone set grows with the
 * relationship (`days_100`, `days_365`, `years_n` …), so a pair long enough
 * passes the default page size and the oldest milestones vanish from the tape
 * with nothing on screen saying so.
 */
export function useAnniversaryMilestones(
  id: string | undefined,
  status?: string,
  options?: Partial<
    UseInfiniteQueryOptions<
      AnniversaryMilestoneListResponse,
      Error,
      InfiniteData<AnniversaryMilestoneListResponse>,
      readonly unknown[],
      string | null
    >
  >,
) {
  return useInfiniteQuery<
    AnniversaryMilestoneListResponse,
    Error,
    InfiniteData<AnniversaryMilestoneListResponse>,
    readonly unknown[],
    string | null
  >({
    queryKey: [...queryKeys.anniversary.milestones(id ?? "", status ?? null)] as const,
    queryFn: ({ pageParam }) => listMilestones(id ?? "", { status, cursor: pageParam }),
    initialPageParam: null as string | null,
    // `null` is the only "no more" the API can express: `has_more` true with a
    // null cursor is the defect this whole change exists to remove, so the walk
    // stops on the cursor rather than on the flag.
    getNextPageParam: (last) => last.next_cursor,
    enabled: Boolean(id),
    ...options,
  });
}

/** Activity log, paged. Same reasoning as {@link useAnniversaryMilestones}. */
export function useAnniversaryActivity(
  id: string | undefined,
  options?: Partial<
    UseInfiniteQueryOptions<
      AnniversaryActivityListResponse,
      Error,
      InfiniteData<AnniversaryActivityListResponse>,
      readonly unknown[],
      string | null
    >
  >,
) {
  return useInfiniteQuery<
    AnniversaryActivityListResponse,
    Error,
    InfiniteData<AnniversaryActivityListResponse>,
    readonly unknown[],
    string | null
  >({
    queryKey: queryKeys.anniversary.activity(id ?? ""),
    queryFn: ({ pageParam }) => listActivity(id ?? "", { cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    enabled: Boolean(id),
    ...options,
  });
}

export function useAnniversaryDiaries(
  id: string | undefined,
  options?: Partial<UseQueryOptions<AnniversaryDiaryListResponse, Error>>,
) {
  return useQuery<AnniversaryDiaryListResponse, Error>({
    queryKey: queryKeys.anniversary.diaries(id ?? ""),
    queryFn: () => getAnniversaryDiaries(id ?? ""),
    enabled: Boolean(id),
    ...options,
  });
}

// ----- Mutations ----------------------------------------------------------

export function useCreateAnniversary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: AnniversaryCreateInput) => createAnniversary(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.anniversary.all });
      void qc.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
}

export function useUpdateAnniversary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: AnniversaryUpdateInput }) =>
      updateAnniversary(id, payload),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: queryKeys.anniversary.all });
      void qc.invalidateQueries({
        queryKey: queryKeys.anniversary.detail(data.id),
      });
      void qc.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
}

export function useDeleteAnniversary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, confirm }: { id: string; confirm?: boolean }) =>
      softDeleteAnniversary(id, { confirm }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.anniversary.all });
      void qc.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
}

export function useRestoreAnniversary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => restoreAnniversary(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.anniversary.all });
      void qc.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
}

export function useLayaClassifyCategory() {
  // Laya is a SUGGEST — exposed as a mutation so callers fire-and-forget.
  return useMutation<AnniversaryClassifyResponse, Error, AnniversaryClassifyInput>({
    mutationFn: (payload) => classifyCategory(payload),
  });
}
